import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Scale, 
  BookOpen, 
  Lock, 
  Cpu, 
  AlertTriangle, 
  FileText, 
  CreditCard, 
  Video, 
  Globe, 
  ChevronRight, 
  ArrowLeft, 
  Printer, 
  CheckCircle2,
  Award,
  Gavel,
  Eye,
  Server
} from 'lucide-react';

export const TermsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('preamble');

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    { id: 'preamble', title: '1. Preamble & Binding Legal Agreement', urdu: 'تمہید اور قانونی معاہدے کی پابندی', icon: Scale },
    { id: 'jurisdiction', title: '2. Legal Framework & International Scope', urdu: 'قانونی دائرہ کار (پاکستان و بین الاقوامی قوانین)', icon: Globe },
    { id: 'disclaimer', title: '3. Professional Legal Disclaimer & Non-Retainer Status', urdu: 'وکالتی ذمہ داری اور عدم قانونی مشورہ کا اعلان', icon: AlertTriangle },
    { id: 'ai-limitations', title: '4. AI Intelligence & Smart Drafting Liability Limits', urdu: 'مصنوعی ذہانت (AI) اور ڈرافٹنگ کے حدودِ ذمہ داری', icon: Cpu },
    { id: 'ip-drm', title: '5. Intellectual Property & Digital Rights Management (DRM)', urdu: 'حقوق دانش، کاپی رائٹ اور ڈیجیٹل رائٹس مینجمنٹ', icon: Lock },
    { id: 'peca-cyber', title: '6. Cybersecurity & PECA 2016 Enforcement', urdu: 'سائبر سیکیورٹی اور پیکا ایکٹ 2016 کا نفاذ', icon: ShieldCheck },
    { id: 'accounts', title: '7. Account Registration, Authentication & KYC', urdu: 'اکاؤنٹ کی توثیق، بار کونسل لائسنس اور کے وائی سی', icon: Award },
    { id: 'billing', title: '8. Subscriptions, Payments & No-Refund Digital Goods Policy', urdu: 'فیس، سبسکرپشن اور عدم واپسی کی پالیسی', icon: CreditCard },
    { id: 'video-conf', title: '9. Virtual Consultations & Video Conferencing Protocols', urdu: 'ویڈیو کانفرنسنگ اور وکیل کلائنٹ رازداری کے اصول', icon: Video },
    { id: 'data-privacy', title: '10. Data Protection & Global Privacy Compliance (GDPR/PECA)', urdu: 'ڈیٹا کا تحفظ، پرائیویسی اور بین الاقوامی معیارات', icon: Server },
    { id: 'indemnification', title: '11. Comprehensive Indemnification & Hold Harmless', urdu: 'تلافی خسارہ اور ہرجانے سے تحفظ', icon: Gavel },
    { id: 'liability-cap', title: '12. Limitation of Financial Liability', urdu: 'مالیاتی ذمہ داری کا حتمی تعین', icon: FileText },
    { id: 'dispute-arbitration', title: '13. Governing Law, Arbitration & Dispute Resolution', urdu: 'مروجہ قوانین، ثالثی (Arbitration) اور تنازعات کا حل', icon: BookOpen },
    { id: 'miscellaneous', title: '14. Severability, Force Majeure & Modifications', urdu: 'متفرق دفعات، ترمیم اور فورس میجر', icon: CheckCircle2 }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0f1d',
      color: '#e2e8f0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* TOP LUXURY HEADER */}
      <header style={{
        backgroundColor: '#0f172a',
        borderBottom: '2px solid #ecc94b',
        padding: '1.25rem 2rem',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#ecc94b',
            textDecoration: 'none',
            fontWeight: '800',
            fontSize: '0.9rem',
            padding: '0.4rem 0.8rem',
            borderRadius: '8px',
            backgroundColor: 'rgba(236,201,75,0.1)',
            border: '1px solid rgba(236,201,75,0.3)'
          }}>
            <ArrowLeft size={16} />
            <span>Back to Portal</span>
          </Link>

          <div>
            <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '900', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Scale size={22} color="#ecc94b" />
              <span>ELUM LAW ASSOCIATES</span>
              <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', backgroundColor: '#1e293b', color: '#ecc94b', borderRadius: '4px', border: '1px solid #ecc94b' }}>
                LEGAL STATUTE 2026
              </span>
            </h1>
            <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
              Master Terms of Service, Digital Governance & AI Intelligence Regulations (Pakistan & Transnational Operations)
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button 
            onClick={() => window.print()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              backgroundColor: '#1e293b',
              color: '#cbd5e1',
              border: '1px solid #334155',
              padding: '0.5rem 0.9rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.82rem',
              fontWeight: '700'
            }}
          >
            <Printer size={15} />
            <span>Print / Save PDF</span>
          </button>
        </div>
      </header>

      {/* HERO BANNER */}
      <div style={{
        background: 'linear-gradient(135deg, #0b132b 0%, #1c2541 50%, #0a0f1d 100%)',
        padding: '3.5rem 2rem 2.5rem 2rem',
        borderBottom: '1px solid #1e293b',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.4rem 1rem',
            borderRadius: '50px',
            backgroundColor: 'rgba(236,201,75,0.12)',
            border: '1.5px solid #ecc94b',
            color: '#ecc94b',
            fontSize: '0.85rem',
            fontWeight: '800',
            marginBottom: '1.25rem',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            <ShieldCheck size={18} />
            <span>Comprehensive Legal Charter & End-User License Agreement (EULA)</span>
          </div>

          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '900',
            color: '#f8fafc',
            lineHeight: '1.25',
            margin: '0 0 1rem 0'
          }}>
            Master Terms of Service & Regulatory Governance Framework
          </h1>
          
          <div style={{
            fontFamily: '"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", serif',
            fontSize: '1.25rem',
            color: '#ecc94b',
            direction: 'rtl',
            lineHeight: '2.2',
            marginBottom: '1.5rem'
          }}>
            جامع شرائط و ضوابط، قانونی اعلامیہ، حق ملکیت دانش اور تحفظِ صارفین کا بین الاقوامی و ملکی منشور
          </div>

          <p style={{
            fontSize: '0.95rem',
            color: '#94a3b8',
            lineHeight: '1.7',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            Effective Date: October 7, 2026 • Governing Jurisdictions: Islamic Republic of Pakistan (Primary Seat), UNCITRAL & International Conventions. Applicable to all users of Elum Law Directory, Smart Legal Drafting Nodes, AI Assistants, Digital Publication Archives, and Virtual Consultation Networks.
          </p>
        </div>
      </div>

      {/* MAIN CONTENT CONTAINER WITH DUAL NAVIGATION */}
      <div style={{
        maxWidth: '1440px',
        margin: '0 auto',
        padding: '2.5rem 2rem',
        display: 'grid',
        gridTemplateColumns: '320px 1fr',
        gap: '2.5rem',
        alignItems: 'start'
      }}>
        {/* SIDEBAR NAVIGATION */}
        <aside style={{
          position: 'sticky',
          top: '90px',
          backgroundColor: '#0f172a',
          borderRadius: '16px',
          border: '1.5px solid #1e293b',
          padding: '1.25rem',
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto'
        }}>
          <div style={{
            paddingBottom: '0.75rem',
            borderBottom: '1px solid #1e293b',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '0.85rem', fontWeight: '900', color: '#ecc94b', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Index of Clauses
            </span>
            <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '700' }}>14 Articles</span>
          </div>

          <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {sections.map((sec) => {
              const Icon = sec.icon;
              const isActive = activeSection === sec.id;
              return (
                <button
                  key={sec.id}
                  onClick={() => scrollTo(sec.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '10px',
                    border: isActive ? '1px solid #ecc94b' : '1px solid transparent',
                    backgroundColor: isActive ? 'rgba(236,201,75,0.12)' : 'transparent',
                    color: isActive ? '#ecc94b' : '#cbd5e1',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    fontSize: '0.8rem',
                    fontWeight: isActive ? '800' : '600',
                    lineHeight: '1.35'
                  }}
                >
                  <Icon size={16} style={{ marginTop: '2px', flexShrink: 0 }} color={isActive ? '#ecc94b' : '#64748b'} />
                  <div>
                    <div>{sec.title}</div>
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: isActive ? '#fef08a' : '#64748b',
                      fontFamily: '"Noto Nastaliq Urdu", serif',
                      direction: 'rtl',
                      marginTop: '2px'
                    }}>
                      {sec.urdu}
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* ARTICLES CONTENT */}
        <main style={{
          backgroundColor: '#0f172a',
          borderRadius: '20px',
          border: '1.5px solid #1e293b',
          padding: '3rem',
          boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          lineHeight: '1.8',
          color: '#cbd5e1'
        }}>
          {/* ARTICLE 1: PREAMBLE */}
          <section id="preamble" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Scale size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                1. Preamble & Legally Binding Agreement (معاہدہ برائے استعمال و شرائط)
              </h2>
            </div>
            
            <p>
              This Master Agreement for Terms of Service, Legal Compliance, and End-User License (hereinafter referred to as the <strong>"Agreement"</strong> or <strong>"Terms"</strong>) constitutes an irrevocable, legally enforceable contract entered into by and between <strong>Elum Law Associates & AI Legal Intelligence Research Network</strong> (hereinafter referred to as <strong>"Elum Law"</strong>, <strong>"Platform"</strong>, <strong>"We"</strong>, <strong>"Us"</strong>, or <strong>"Company"</strong>) and any individual, advocate, legal practitioner, law firm, corporate entity, academic institution, or student (hereinafter referred to as the <strong>"User"</strong>, <strong>"Subscriber"</strong>, or <strong>"You"</strong>) accessing or utilizing any software, database, API, Smart Legal Drafting utility, case law indexing, digital monographs, or virtual conference nodes provided on <code>https://elumlaw.com</code> or affiliated subdomains.
            </p>

            <div style={{
              backgroundColor: 'rgba(236,201,75,0.08)',
              borderLeft: '4px solid #ecc94b',
              padding: '1.25rem 1.5rem',
              borderRadius: '0 10px 10px 0',
              margin: '1.5rem 0'
            }}>
              <strong style={{ color: '#ecc94b' }}>CRITICAL LEGAL ACKNOWLEDGMENT:</strong> By clicking "Sign Up", "Log In", "Read Online", "Download", "Generate Legal Draft", or otherwise browsing this website, you explicitly acknowledge that you have read, understood, and consented to be bound by the entirety of these Terms pursuant to <strong>Section 3, 7, and 8 of the Electronic Transactions Ordinance, 2002 (ETO 2002)</strong> and <strong>Section 10 of the Contract Act, 1872 (Pakistan)</strong>. If you do not agree to every clause herein, you must immediately terminate your session and refrain from using this Platform.
            </div>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 2: JURISDICTION */}
          <section id="jurisdiction" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Globe size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                2. Multi-Jurisdictional Legal Framework & International Scope (قانونی دائرہ کار)
              </h2>
            </div>

            <p>
              Elum Law operates under the constitutional and statutory sovereignty of the Islamic Republic of Pakistan, while simultaneously integrating international private law standards for cross-border subscriptions, intellectual property recognition, and digital commerce.
            </p>

            <h3 style={{ color: '#f1f5f9', fontSize: '1.15rem', marginTop: '1.5rem' }}>2.1 Domestic Statutory Framework (Pakistan):</h3>
            <ul style={{ paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <li><strong>Constitution of the Islamic Republic of Pakistan, 1973:</strong> Article 4 (Right of individuals to be dealt with in accordance with law), Article 10A (Right to fair trial), Article 19A (Right to information), and Article 227 (Provisions relating to the Holy Quran and Sunnah).</li>
              <li><strong>Electronic Transactions Ordinance, 2002 (ETO):</strong> Statutory recognition of electronic records, digital documentation, and online commercial consent.</li>
              <li><strong>Prevention of Electronic Crimes Act, 2016 (PECA):</strong> Stringent criminal prosecution and civil penalties for cyber offenses, unauthorized database copying, digital forgery, and system intrusion.</li>
              <li><strong>Copyright Ordinance, 1962 & Patents Ordinance, 2000:</strong> Complete statutory protection of proprietary case law editorial headnotes, solved examinations, algorithms, and publications.</li>
              <li><strong>Legal Practitioners and Bar Councils Act, 1973 & Canons of Professional Conduct:</strong> Absolute compliance with ethical boundaries and prohibitions on unlawful practice of law.</li>
              <li><strong>Arbitration Act, 1940:</strong> Exclusive alternative dispute resolution mechanism for contractual claims.</li>
            </ul>

            <h3 style={{ color: '#f1f5f9', fontSize: '1.15rem', marginTop: '1.5rem' }}>2.2 International Treaties & Cross-Border Framework:</h3>
            <ul style={{ paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <li><strong>Berne Convention & WIPO Copyright Treaty (WCT):</strong> Automatic worldwide protection for all monographs, solved books, and scholarly works.</li>
              <li><strong>TRIPS Agreement (WTO):</strong> Enforcement of intellectual property rights and trade secrets across all member states.</li>
              <li><strong>UNCITRAL Model Law on Electronic Commerce:</strong> Cross-border legal validity of digital subscriptions and automated smart agreements.</li>
              <li><strong>EU General Data Protection Regulation (GDPR) / UK GDPR / California Consumer Privacy Act (CCPA/CPRA):</strong> High-standard data subject rights for international users.</li>
              <li><strong>New York Convention on the Recognition and Enforcement of Foreign Arbitral Awards (1958):</strong> Full transnational enforceability of arbitral rulings.</li>
            </ul>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 3: DISCLAIMER */}
          <section id="disclaimer" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <AlertTriangle size={28} color="#f59e0b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                3. Professional Legal Disclaimer & Non-Retainer Status (وکالتی ذمہ داری کا اعلان)
              </h2>
            </div>

            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1.5px solid #ef4444',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ color: '#ef4444', margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: '900' }}>
                ⚠️ STRICT PROHIBITION OF AUTOMATED ATTORNEY-CLIENT RELATIONSHIP
              </h4>
              <p style={{ margin: 0, fontSize: '0.92rem', lineHeight: '1.7', color: '#fca5a5' }}>
                The contents, case law summaries, AI search matrices, Smart Legal Drafting generators, statutory explanations, and examination solutions available on this Platform are intended solely for informational, educational, and legal research assistance. <strong>NOTHING ON THIS WEBSITE CONSTITUTES BINDING LEGAL ADVICE, NOR DOES YOUR USE OF ANY AUTOMATED FEATURE ESTABLISH AN ADVOCATE-CLIENT PRIVILEGED RELATIONSHIP.</strong>
              </p>
            </div>

            <p>
              3.1 <strong>Independence of Retainership:</strong> An advocate-client relationship with Elum Law Associates or any advocate registered on the Directory is established <em>only</em> upon the execution of a formal, written <strong>Vakalatnama</strong> or retainership agreement, accompanied by agreed legal fees and client identity verification under the Pakistan Legal Practitioners and Bar Councils Rules, 1976.
            </p>
            <p>
              3.2 <strong>Mandatory Practitioner Review:</strong> Any legal document, plaint, written statement, constitutional petition under Article 199/184(3), criminal appeal under Cr.P.C., or contractual instrument generated through Elum Law’s AI or templates must be independently scrutinized, verified for citation veracity, and vetted by a licensed Advocate High Court or Supreme Court before filing in any judicial or quasi-judicial forum.
            </p>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 4: AI LIMITATIONS */}
          <section id="ai-limitations" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Cpu size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                4. AI Intelligence, Generative Algorithms & Drafting Liability Limitations (مصنوعی ذہانت کے حدود)
              </h2>
            </div>

            <p>
              Elum Law incorporates advanced artificial intelligence, machine learning embeddings, and natural language processing architectures to index judgments of the Supreme Court of Pakistan, Federal Constitutional Court (FCC), Federal Shariat Court, and High Courts (Lahore, Sindh, Peshawar, Balochistan, Islamabad).
            </p>

            <ul style={{ paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <li>
                <strong>Heuristic & Probabilistic Nature:</strong> The User recognizes that Generative AI models operate on statistical probabilistic algorithms. While Elum Law enforces strict citation matching against verified law reports (PLD, SCMR, CLC, YLR, MLD, PLC, PTD, PCRLJ), AI outputs may occasionally exhibit hallucinations, omissions, or obsolete statutory references.
              </li>
              <li>
                <strong>Zero Judicial Warranty:</strong> Elum Law gives no express or implied warranty regarding the legal outcome of any litigation, bail petition, suit for declaration, or commercial dispute based on drafts or research generated by the AI node.
              </li>
              <li>
                <strong>Exclusion of Liability for Adverse Court Orders:</strong> Under no circumstances shall Elum Law, its managing partners, software engineers, or AI architects be held liable for dismissals, contempt proceedings, procedural rejections, or costs levied by any Court of Law due to inaccuracies in AI-assisted drafts.
              </li>
            </ul>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 5: IP & DRM */}
          <section id="ip-drm" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Lock size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                5. Intellectual Property Rights & Digital Rights Management (DRM) Shield (حقوقِ دانش و ڈی آر ایم)
              </h2>
            </div>

            <p>
              All proprietary software, algorithms, editorial headnotes, solved CSS/PMS exam manuals, bilingual monographs (including <em>الْأَرْبَعِينَ فِي الشَّفَاعَةِ الْكُبْرَىٰ</em> and jurisprudence treatises), UI layouts, graphics, audio tracks, and presentation slides are the exclusive intellectual property of Elum Law Associates, protected under the <strong>Copyright Ordinance, 1962</strong> and the <strong>Berne Convention</strong>.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1.5rem',
              margin: '1.5rem 0'
            }}>
              <div style={{
                backgroundColor: '#1e293b',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #334155'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontWeight: '800', marginBottom: '0.5rem' }}>
                  <Eye size={18} />
                  <span>Free Online Reading License</span>
                </div>
                <p style={{ fontSize: '0.85rem', margin: 0, color: '#cbd5e1' }}>
                  Permits free, non-exclusive, non-transferable personal viewing via our DRM-protected browser viewer. Direct copying, text scraping, print-screen extraction, developer tools manipulation, or commercial exploitation is strictly prohibited.
                </p>
              </div>

              <div style={{
                backgroundColor: '#1e293b',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #334155'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#ecc94b', fontWeight: '800', marginBottom: '0.5rem' }}>
                  <CreditCard size={18} />
                  <span>Paid Download / Audio Subscription</span>
                </div>
                <p style={{ fontSize: '0.85rem', margin: 0, color: '#cbd5e1' }}>
                  Grants a single-user personal license for offline reference. Redistribution, uploading to torrents, peer-to-peer file sharing, resale, or institutional pooling without corporate multi-seat licenses constitutes criminal infringement.
                </p>
              </div>
            </div>

            <p>
              5.1 <strong>Forensic Steganographic Watermarking:</strong> All digital assets and reading streams are cryptographically watermarked with the User's verified email, IP address, timestamp, and unique transaction token. Any leaked document found in the public domain will be forensically traced, resulting in immediate criminal prosecution under <strong>Section 66 of the Copyright Ordinance, 1962</strong> and <strong>PECA 2016</strong>.
            </p>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 6: PECA 2016 */}
          <section id="peca-cyber" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <ShieldCheck size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                6. Cybersecurity Enforcement & PECA 2016 Criminal Injunctions (سائبر سیکیورٹی قوانین)
              </h2>
            </div>

            <p>
              Users agree to strictly respect the technological integrity of Elum Law. Any attempt to compromise server infrastructure, bypass DRM locks, execute SQL injections, inject malicious scripts, scrape judgments via unauthorized bots, or harvest user credentials will trigger immediate legal action under the <strong>Prevention of Electronic Crimes Act, 2016 (PECA)</strong>:
            </p>

            <ul style={{ paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <li><strong>Section 3 (Unauthorized Access to Information System):</strong> Subject to rigorous imprisonment and statutory fines.</li>
              <li><strong>Section 4 (Unauthorized Copying or Transmission of Data):</strong> Penalties for illegal scraping and data exfiltration.</li>
              <li><strong>Section 5 (Interference with Information System or Data):</strong> Legal remedies for DDOS or server disruptions.</li>
              <li><strong>Section 10 (Cyber Terrorism & Compromise of Critical Legal Archives):</strong> Non-bailable criminal offense reported directly to the FIA Cyber Crime Wing.</li>
              <li><strong>Section 13 & 14 (Electronic Forgery and Fraud):</strong> Criminal prosecution for falsifying registration credentials or payment slips.</li>
            </ul>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 7: ACCOUNTS & KYC */}
          <section id="accounts" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Award size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                7. Account Registration, Authentication & KYC Verification (اکاؤنٹ اور کے وائی سی)
              </h2>
            </div>

            <p>
              7.1 <strong>Accuracy of Information:</strong> Advocates, legal researchers, and clients registering on the platform must provide true, complete, and verifiable credentials, including Bar Council Enrollment Numbers, CNIC/Passport, and official contact details.
            </p>
            <p>
              7.2 <strong>Admin & Superuser Verification:</strong> Elum Law reserves the unreserved discretion to approve, reject, or audit any registration. Accounts created with fraudulent Bar credentials will be suspended and reported to the respective Provincial Bar Council (Punjab, Sindh, Balochistan, KP, or Islamabad Bar Council).
            </p>
            <p>
              7.3 <strong>Account Sharing Prohibition:</strong> Subscriptions are single-user per seat. Concurrent logins from disparate geographical IP addresses trigger automated algorithmic rate-limiting and temporary account lockdown.
            </p>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 8: BILLING */}
          <section id="billing" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <CreditCard size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                8. Subscriptions, Payments & No-Refund Digital Goods Policy (فیس اور عدم واپسی کی پالیسی)
              </h2>
            </div>

            <p>
              8.1 <strong>Payment Channels:</strong> Subscriptions, digital monograph purchases, solved examination manuals, and video consultation credits may be acquired via authorized gateways including JazzCash, Easypaisa, 1Link Online Banking, Raast, Direct Bank Transfer, or International Credit/Debit Cards.
            </p>
            <p>
              8.2 <strong>Taxes & Withholding:</strong> All payments are subject to applicable federal and provincial sales taxes on services (PRA, BRA, SRB, KPRA) and withholding taxes pursuant to the Income Tax Ordinance, 2001.
            </p>
            <p>
              8.3 <strong>Strict No-Refund Policy on Digital Goods:</strong> In accordance with standard international e-commerce practices for digital goods and intangible assets, <strong>NO REFUNDS, CHARGEBACKS, OR CREDITS</strong> will be provided once a publication, PDF document, solved paper, or AI drafting quota has been delivered or accessed online.
            </p>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 9: VIDEO CONSULTATION */}
          <section id="video-conf" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Video size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                9. Virtual Legal Consultations & Video Conferencing Protocols (ویڈیو کانفرنسنگ کے اصول)
              </h2>
            </div>

            <p>
              9.1 <strong>Encrypted Transmission Conduit:</strong> Elum Law provides secure, peer-to-peer WebRTC and end-to-end encrypted video conferencing infrastructure to facilitate virtual legal meetings between verified advocates and clients.
            </p>
            <p>
              9.2 <strong>Privileged Communications:</strong> Statutory attorney-client privilege under <strong>Articles 9 to 12 of the Qanun-e-Shahadat Order, 1984 (QSO 1984)</strong> applies solely between the attending counsel and the client. Elum Law operates solely as the technical carrier and does not record or intercept private consultations without explicit dual-party consent.
            </p>
            <p>
              9.3 <strong>Counsel Conduct & Regulatory Compliance:</strong> Advocates conducting virtual consultations remain personally responsible for complying with the High Court Rules and Orders, ethical decorum, and conflict-of-interest checks.
            </p>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 10: DATA PRIVACY */}
          <section id="data-privacy" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Server size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                10. Data Protection & Global Privacy Compliance (ڈیٹا پروٹیکشن و پرائیویسی)
              </h2>
            </div>

            <p>
              Elum Law implements bank-grade security protocols, including TLS 1.3 encryption in transit, AES-256 encryption at rest, and automated access audits.
            </p>
            <p>
              10.1 <strong>Data Subject Rights (GDPR & CCPA Compliant):</strong> International and domestic users enjoy full rights to request access, rectification, data portability, and erasure of their personal profile data, subject only to mandatory statutory recordkeeping requirements under Pakistan tax and bar regulations.
            </p>
            <p>
              10.2 <strong>Non-Disclosure & Third-Party Sharing:</strong> We do not monetize, sell, or rent your private case files, draft submissions, or contact databases to third-party ad networks.
            </p>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 11: INDEMNIFICATION */}
          <section id="indemnification" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <Gavel size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                11. Comprehensive Indemnification & Hold Harmless (تلافیِ خسارہ)
              </h2>
            </div>

            <p>
              The User agrees to defend, indemnify, and hold harmless Elum Law Associates, its partners, advocates, directors, officers, software architects, AI researchers, and agents from and against any and all claims, liabilities, losses, damages, fines, Bar Council inquiries, judicial penalties, or expenses (including reasonable legal fees) arising out of or related to:
            </p>
            <ul style={{ paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <li>Your breach of any provision of this Agreement or violation of applicable laws.</li>
              <li>Your submission of unvetted or inaccurate AI-generated drafts to any Court or Tribunal.</li>
              <li>Your unauthorized reproduction, redistribution, or DRM violation of proprietary publications.</li>
              <li>Any defamatory, tortious, or unlawful material submitted through your user account.</li>
            </ul>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 12: LIABILITY CAP */}
          <section id="liability-cap" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <FileText size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                12. Maximum Limitation of Financial Liability (مالیاتی ذمہ داری کا تعین)
              </h2>
            </div>

            <p>
              TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW, IN NO EVENT SHALL ELUM LAW ASSOCIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES (INCLUDING LOSS OF PROFITS, LITIGATION PROSPECTS, DATA LOSS, OR BUSINESS INTERRUPTION).
            </p>
            <p>
              <strong>AGGREGATE MONETARY CAP:</strong> THE TOTAL CUMULATIVE LIABILITY OF ELUM LAW ASSOCIATES FOR ALL CLAIMS OF ANY KIND ARISING UNDER OR RELATING TO THIS PLATFORM SHALL NOT EXCEED THE ACTUAL AMOUNT PAID BY THE SUBSCRIBER TO ELUM LAW IN THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE CLAIM, OR PKR 5,000 (PAKISTANI RUPEES FIVE THOUSAND ONLY), WHICHEVER IS LOWER.
            </p>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 13: DISPUTE RESOLUTION & ARBITRATION */}
          <section id="dispute-arbitration" style={{ marginBottom: '3.5rem', scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <BookOpen size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                13. Governing Law, Binding Arbitration & Dispute Resolution (مروجہ قوانین و ثالثی)
              </h2>
            </div>

            <p>
              13.1 <strong>Governing Law:</strong> This Agreement shall be governed by, construed, and enforced exclusively in accordance with the laws of the Islamic Republic of Pakistan, without giving effect to conflict of laws principles.
            </p>
            <p>
              13.2 <strong>Two-Tier Dispute Resolution:</strong>
            </p>
            <ol style={{ paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <li>
                <strong>Amicable Negotiation:</strong> In the event of any controversy, claim, or dispute, the parties shall first attempt in good faith to resolve the dispute through direct formal consultation within thirty (30) business days from written notice.
              </li>
              <li>
                <strong>Mandatory Binding Arbitration:</strong> If the dispute is not resolved amicably within 30 days, it shall be referred to and finally resolved by binding arbitration under the <strong>Arbitration Act, 1940 (Pakistan)</strong>. The seat and legal place of arbitration shall be Quetta / Islamabad, Pakistan. The arbitration shall be conducted in the English language by a single sole arbitrator appointed by mutual agreement or by the High Court. The arbitral award shall be final, conclusive, and binding upon both parties.
              </li>
            </ol>
            <p>
              13.3 <strong>International Enforcement:</strong> For international subscribers, any arbitral award rendered under this clause shall be fully enforceable in foreign jurisdictions under the <strong>New York Convention on the Recognition and Enforcement of Foreign Arbitral Awards (1958)</strong>.
            </p>
            <p>
              13.4 <strong>Injunctive Relief:</strong> Notwithstanding the arbitration clause, Elum Law retains the absolute right to seek immediate interlocutory injunctive relief in any court of competent jurisdiction to restrain copyright infringement, DRM bypass, or cyber security breaches.
            </p>
          </section>

          <hr style={{ borderColor: '#1e293b', margin: '3rem 0' }} />

          {/* ARTICLE 14: MISCELLANEOUS */}
          <section id="miscellaneous" style={{ scrollMarginTop: '100px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <CheckCircle2 size={28} color="#ecc94b" />
              <h2 style={{ fontSize: '1.65rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                14. Severability, Force Majeure & Ongoing Modifications (متفرق شقیں اور ترامیم)
              </h2>
            </div>

            <p>
              14.1 <strong>Severability:</strong> If any provision of these Terms is held by an arbitral tribunal or court of competent jurisdiction to be invalid, void, or unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
            <p>
              14.2 <strong>Force Majeure:</strong> Neither party shall be liable for failure or delay in performance resulting from causes beyond reasonable control, including acts of God, governmental statutory shifts, telecommunication gateway interruptions, or judicial decrees.
            </p>
            <p>
              14.3 <strong>Entire Agreement:</strong> These Terms constitute the entire understanding between the User and Elum Law Associates regarding the use of this digital infrastructure, superseding all prior verbal or written understandings.
            </p>
            <p>
              14.4 <strong>Modifications:</strong> Elum Law reserves the right to modify or update these Terms at any time. Material revisions will be posted on this page with an updated Effective Date. Continued use of the platform constitutes unconditional acceptance of the revised charter.
            </p>

            <div style={{
              marginTop: '2.5rem',
              padding: '1.5rem',
              backgroundColor: '#1e293b',
              borderRadius: '12px',
              border: '1.5px solid #ecc94b',
              textAlign: 'center'
            }}>
              <h4 style={{ color: '#ecc94b', margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
                Legal & Regulatory Inquiries / Notice Address
              </h4>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#cbd5e1' }}>
                Elum Law Associates Legal Directorate • AI Intelligence Node<br />
                Headquarters: Quetta & Islamabad, Pakistan • Official Portal: <code>https://elumlaw.com</code><br />
                Direct Legal Notice Contact: <code>legal@elumlaw.com</code> • Contact Registrar: <code>info@elumlaw.com</code>
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default TermsPage;
